import { useDispatchWindowResize } from '@bridebook/web/app-basic/components/hooks/use-dispatch-window-resize';
import { useGlobalABTests } from '@bridebook/web/app-basic/lib/ab-tests/hooks/use-global-ab-tests';
import { getAlternateLinks } from '@bridebook/web/app-basic/lib/app/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { Router as NextRouter } from 'next/dist/client/router';
import { useRouter } from 'next/router';
import { FC, PropsWithChildren } from 'react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LocaleChangeSuggestion } from '@bridebook/web/app-shared/components/locale-change-suggestion/locale-change-suggestion';
import { AppBannerScript, MetaRouterScript } from '@bridebook/web/app-shared/lib/app/scripts';
import { env } from '@bridebook/web/lib/env';
import addUKPluralUpdate from '@bridebook/web/lib/i18n/addUKPluralUpdate';
import { UrlHelper } from '@bridebook/web/lib/url-helper';

export interface LayoutBasicProps {
  router: NextRouter;
}

const EXCLUDED_SUGGESTION_URLS = [UrlHelper.for.main, UrlHelper.addressCollector.main];
const normalizePathname = (path: string) => (path.endsWith('/') ? path : `${path}/`);

export const LayoutBasic: FC<PropsWithChildren<LayoutBasicProps>> = ({ children, router }) => {
  addUKPluralUpdate();
  useDispatchWindowResize();
  useGlobalABTests();
  const { query, pathname } = useRouter();
  /**
   * Query supplier param is added in cms, for previewing supplier profile. For this case we don't
   * want to show locale change suggestion.
   * We also don't want to show locale change suggestion when users are visiting a couple's wedding website.
   */
  const hideLocaleChangeSuggestion =
    query.supplier ||
    EXCLUDED_SUGGESTION_URLS.some((excludedPath) =>
      pathname.includes(normalizePathname(excludedPath)),
    );
  const showReactQueryDevtools = Object.keys(router.query).includes('reactQuery');

  const alternateLinks = useSelector(getAlternateLinks);
  return (
    <>
      <AppBannerScript showBanner />
      <MetaRouterScript isCordova={false} />
      {!hideLocaleChangeSuggestion && <LocaleChangeSuggestion alternateLinks={alternateLinks} />}
      {children}
      {!env.IS_PRODUCTION_BUILD && showReactQueryDevtools && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </>
  );
};
